// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  createMachine,
  state,
  state as final,
  state as initial,
  transition,
  reduce,
  guard,
  invoke,
  immediate,
} from 'robot3';
import { createUseMachine } from 'robot-hooks';
import { ACTION_TYPES, FRESH_PRODUCT_TYPES, STATE } from './constants.js';
import {
  hasCustomer,
  hasNoCustomer,
  hasCompletePet,
  hasPetName,
  hasPetWeight,
  hasPetActivity,
  allPetsHaveProductInCart,
  isMixed,
  isFresh,
  submitCartFailed,
  isCompletePet,
  allPetsComplete,
  showRecommendedPlan,
  selectedDry,
  noPersonalizedPlan,
  isAddPup,
  hasInitialPetWeight,
  hasInitialPetActivity,
  isInitialFlow,
  isEditPet,
  notEditPet,
  hasPet,
  isDeletePet,
  initialPetComplete,
} from './guard';
import { goBackReducer, saveCart } from './functions';

export const useMachine = createUseMachine(useEffect, useState);

const createInitialContext = ({
  customer = {},
  pet = {},
  pet_id = '',
  recipes = { fresh: null, dry: null },
  addons = [],
  planType = null,
  stateKey = STATE.initial,
  nextKey = null,
  prevState = {},
  flags = {},
} = {}) => ({
  customer,
  pet,
  pet_id,
  recipes,
  addons,
  planType,
  stateKey,
  nextKey,
  prevState,
  flags,
});

export const onboardingMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      /**
       ** Transitions to Pup Info Page if page by default
       ** Pup Info then takes over and passes the pet to the correct step via immediate transitions
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupInfo,
        guard(isAddPup),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
          customer: payload.customer,
          pets: payload.pets,
          pet: null,
          pet_id: '',
        })),
      ),
      /**
       ** Transitions to Pup Info Page for edit pet flow
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.recommendedPlan,
        guard(isEditPet),
        guard(initialPetComplete),
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.birthday,
          customer: payload.customer,
          isEditPet: true,
          pets: payload.pets,
          pet:
            payload.pets.find(p => p.id === payload.pet_id) ?? payload.pets[0],
          pet_id: payload.pet_id,
        })),
      ),
      /**
       ** Transitions to Delete Pet state if
       ** - hasCustomer: true
       ** - hasPet: true
       ** - pets.length > 1
       ** - isDeletePet: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.deletePet,
        guard(hasCustomer),
        guard(hasPet),
        guard(isDeletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: payload.flags,
          prevState: { ...ctx },
          stateKey: STATE.deletePet,
          customer: payload.customer,
          pet_id: payload.pet_id,
          pet: payload.pets.find(p => p.id === payload.pet_id),
          deletePetFromCart: payload.deletePetFromCart,
        })),
      ),
      /**
       ** Transitions to Checkout Page if
       ** Refactor needed for Shopify
       ** - hasCustomer: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.checkout,
        guard(hasCustomer),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.checkout,
            nextKey: 'done',
            customer: payload.customer,
            pet: null,
            pet_id: null,
          };
        }),
      ),

      /**
       ** Transitions to Fresh Recipe Selection Page if

       ** - isFreshUrl: true
       ** - hasFreshInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce((ctx, { payload }) => {
          const pet =
            payload.pets?.find(pet =>
              payload.pets.find(pet => !pet.starter_box),
            ) ?? payload.pets[0];
          return {
            ...ctx,
            flags: payload.flags,
            customer: payload.customer,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.checkout,
            pet,
            pets: payload.pets,
            pet_id: pet?.id ?? '',
            recipes: payload.recipes ?? ctx.recipes,
            fresh: payload.recipes,
            planType: payload.planType ?? ctx.planType,
            isAppSession: payload.isAppSession || ctx.isAppSession,
          };
        }),
      ),

      /**
       ** Transitions to User Info Page if
       ** Refactor needed for Shopify - ES-1474
       ** - hasIncompletePetWithActivity: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.userInfo,
        guard(allPetsComplete),
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => {
          const pet = payload.pets[0];
          return {
            ...ctx,
            flags: { ...ctx.flags, ...payload.flags },
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.planSelection,
            customer: payload.customer,
            pet: pet,
            pet_id: pet?.id ?? '',
          };
        }),
      ),

      /**
       ** Transitions to Pup Info Page if page by default
       ** Pup Info then takes over and passes the pet to the correct step via immediate transitions
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupInfo,
        reduce((ctx, { payload }) => {
          let pet = null;
          if (payload.pets?.length) {
            pet =
              payload.pets.find(pet => !isCompletePet(pet)) ?? payload.pets[0];
          }
          return {
            ...ctx,
            emailStepLast: payload.emailStepLast,
            flags: payload.flags,
            prevState: { ...ctx },
            stateKey: STATE.pupInfo,
            nextKey: STATE.birthday,
            customer: payload.customer,
            pet: pet,
            pet_id: pet?.id ?? '',
          };
        }),
      ),
    ),
    [STATE.pupInfo]: state(
      immediate(
        STATE.calories,
        guard(isInitialFlow),
        guard(notEditPet),
        guard(hasPetName),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.calories,
            nextKey: STATE.activity,
            pet: ctx?.pet,
            pet_id: ctx?.pet?.id,
            initialFlow: true,
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Calories Page if
       */
      transition(
        ACTION_TYPES.toCalories,
        STATE.calories,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.calories,
            nextKey: STATE.activity,
            pet: payload.pet,
            pet_id: payload.pet.id,
          };
        }),
      ),
    ),
    [STATE.calories]: state(
      immediate(
        STATE.activity,
        guard(isInitialFlow),
        guard(notEditPet),
        guard(hasInitialPetWeight),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.userInfo,
          pet: ctx?.pet,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupInfo Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupInfo,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupInfo,
          nextKey: STATE.calories,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Activity Selection Page if
       ** - hasPetWeight: true
       */
      transition(
        ACTION_TYPES.toActivity,
        STATE.activity,
        guard(hasPetWeight),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.activity]: state(
      immediate(
        STATE.userInfo,
        guard(isInitialFlow),
        guard(notEditPet),
        guard(hasInitialPetActivity),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.toUserInfo,
          pet: ctx?.pet,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
        })),
      ),
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.fresh,
          pet: payload.pet,
          pet_id: payload.pet.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Calories Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.calories,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.calories,
          nextKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to User Info Page if
       ** - hasPetActivity: true
       ** - skipAllergiesQuestion: true
       */
      transition(
        ACTION_TYPES.toUserInfo,
        STATE.userInfo,
        // guard(skipAllergiesQuestion),
        guard(hasPetActivity),
        guard(hasNoCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.allergies]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Activity Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to User Info page if
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toUserInfo,
        STATE.userInfo,
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          plan: payload,
        })),
      ),
    ),
    [STATE.userInfo]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Back to Activity Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.userInfo,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Meal Prep Selection Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.customerInfoSaved,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => {
          let nextKey;

          let pet = (ctx.pet || payload.pet) ?? payload?.pets[0] ?? null;

          nextKey =
            ctx.flags.extrasPageCRO !== 'no_page'
              ? STATE.addons
              : STATE.checkout;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey,
            customer: payload.customer,
            pet,
            pet_id: pet.id,
            flags: {
              ...ctx.flags,
              recommendedPageCRO: true,
            },
          };
        }),
      ),
    ),
    [STATE.recommendedPlan]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => {
          let nextKey;

          nextKey = ctx.flags.showRecommendedPlan
            ? STATE.recommendedPlan
            : STATE.planSelection;
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey,
            pet: payload.pet,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Personalize Plan Button
       *
       ** Transitions to Plan Selection/Meal Prep
       **
       */
      transition(
        ACTION_TYPES.toPlanSelection,
        STATE.planSelection,
        guard(hasCustomer),
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
          nextKey: STATE.fresh,
          customer: payload.customer ?? ctx.customer,
          personalizedPlan: true,
          pet: payload.pet ?? ctx.pet,
          pets: payload.pets ?? ctx.pets,
          pet_id: payload.pet.id ?? ctx.pet.id,
        })),
      ),

      transition(
        ACTION_TYPES.selectedMixedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
            planType: payload.planType,
            recipes: { dry: payload.recipes.dry, fresh: payload.recipes.fresh },
          };
        }),
      ),

      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
            planType: payload.planType,
            recipes: { dry: null, fresh: payload.recipes },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout
       **
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsHaveProductInCart),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
        })),
      ),
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.recommendedPlan,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.addons,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
        })),
      ),
    ),
    [STATE.planSelection]: state(
      immediate(
        STATE.recommendedPlan,
        guard(showRecommendedPlan),
        guard(hasCustomer),
        guard(allPetsComplete),
        guard(noPersonalizedPlan),
        reduce(({ ...ctx }, { payload }) => {
          const nextKey = ctx?.flags?.recommendedPageCRO
            ? STATE.addons
            : STATE.checkout;
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey,
            customer: ctx.customer,
            pet: ctx.pet ?? payload.pet,
            pet_id: ctx.pet.id ?? payload.pet.id,
          };
        }),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Recommended Plan page if
       ** - showRecommendedPlan: true
       **
       ** Note: goBack action for when user goes to plan selection from full fresh recommended
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        guard(showRecommendedPlan),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info page if
       ** - hasCustomer: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.planSelection,
          pet: payload.pet,
        })),
        reduce(goBackReducer),
      ),

      /**
       ** Route: Continue/Next (Any Product Card Selected that is not Baked)
       *
       ** Transitions to Fresh Recipe Selection Page
       */
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.baked,
        guard(selectedDry),
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };
          let nextKey;
          if (FRESH_PRODUCT_TYPES.includes(payload.planType)) {
            recipes.dry = null;

            nextKey = STATE.addons;
          } else {
            nextKey = STATE.baked;
          }
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey,
            planType: payload.planType ?? ctx.planType,
            recipes,
          };
        }),
      ),
      /**
       ** Route: Continue/Next (Any Product Card Selected that is not Baked)
       *
       ** Transitions to Fresh Recipe Selection Page
       */
      transition(
        ACTION_TYPES.selectedPlanType,
        STATE.fresh,
        reduce((ctx, { payload }) => {
          const recipes = { ...ctx.recipes };

          let nextKey;
          if (FRESH_PRODUCT_TYPES.includes(payload.planType)) {
            recipes.dry = null;

            nextKey = STATE.addons;
          } else {
            nextKey = STATE.baked;
          }
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey,
            planType: payload.planType ?? ctx.planType,
            mealplanId: payload.mealplanId ?? ctx.mealplanId,
            recipes,
          };
        }),
      ),
    ),
    [STATE.fresh]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Meal Prep Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
            recipes: { ...ctx.recipes, fresh: payload.recipes },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Baked Recipe Selection Page if
       ** - isMixed: true
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.baked,
        guard(isMixed),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
            recipes: {
              ...ctx.recipes,
              fresh: payload.recipes || ctx.recipes.fresh,
            },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Handling browser forward button
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.toAddons,
        STATE.addons,
        guard(isFresh),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
      ),
    ),
    [STATE.baked]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Fresh Recipe Selection Page if
       ** - isMixed: true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.fresh,
        guard(isMixed),
        reduce(ctx => {
          return {
            ...ctx,
            stateKey: STATE.fresh,
            nextKey: STATE.baked,
            planType: ctx.planType,
          };
        }),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button Alternative
       *
       ** Transitions to Meal Prep Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.planSelection,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.planSelection,
          nextKey: STATE.baked,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Addon Selection Page
       */
      transition(
        ACTION_TYPES.selectedBakedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.baked,
            nextKey: STATE.addons,
            recipes: {
              ...ctx.recipes,
              dry: payload.recipes || ctx.recipes.dry,
            },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Handling browser forward button
       ** - isFresh: true
       */
      transition(
        ACTION_TYPES.toAddons,
        STATE.addons,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
      ),
    ),
    [STATE.submitCart]: invoke(
      saveCart,
      /**
       ** Transitions to Fresh Recipe Selection Page if
       ** - submitCartFailed: true
       */
      transition(
        'done',
        STATE.recommendedPlan,
        guard(submitCartFailed),
        guard(showRecommendedPlan),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.recommendedPlan,
        })),
      ),

      transition(
        'done',
        STATE.addons,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.addons,
          nextKey: STATE.checkout,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       */
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.checkout,
        })),
      ),
    ),
    [STATE.addons]: state(
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
        })),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.fresh,
            nextKey: STATE.addons,
          };
        }),
        reduce(goBackReducer),
      ),
    ),
    [STATE.deletePet]: invoke(
      async ctx => {
        return ctx.deletePetFromCart(ctx.pet_id);
      },
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, { data }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          cart: data.cart,
          pets: data.pets,
          pet: data.pets[0],
          pet_id: data.pets[0].id,
        })),
      ),
      transition(
        'error',
        STATE.checkout,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.checkout,
        })),
      ),
    ),
    [STATE.checkout]: final(),
  },
  createInitialContext,
);
